import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { RoundButton } from '@ulule/owl-kit-components'
import * as duvet from '@ulule/duvet'
import { t, tn } from '@ulule/localize'
import { text } from '@owl-nest/utils'

import { getComingSoonLabel, getProgress, getShortCountdown, getFinishTrad, getStatus } from '$models/projects'
import { updateUserRole, saveAction } from '../../features/init/services'

const USER_ROLE_STATUS = {
  neutral: null,
  listed: 'fan',
  created: 'owner',
  backed: 'supporter',
}

const ProjectCardWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  min-width: 230px;
  max-width: 310px;
`

const Button = styled(RoundButton)`
  position: relative;
  padding: 10px;
`

const getRelatedUserRole = (userRole, isLikeCompact) => {
  switch (userRole) {
    case 'fan': {
      const fanLabel = isLikeCompact ? t('Listed') : t('Signed up for the launch')
      return {
        status: 'listed',
        label: fanLabel,
        apiAction: 'unlike',
        roundButton: {
          text: fanLabel,
          icon: 'active-heart',
          isCompact: isLikeCompact,
        },
      }
    }

    case 'supporter':
      return {
        status: 'backed',
        label: t('Backed'),
        apiAction: null,
        roundButton: {
          text: t('Backed'),
          icon: 'backed',
          isCompact: isLikeCompact,
        },
      }

    case 'owner':
    case 'editor':
    case 'moderator':
      return {
        status: 'created',
        label: t('Created'),
        apiAction: null,
        roundButton: {
          text: t('Created'),
          icon: 'created',
          isCompact: isLikeCompact,
        },
      }

    default: {
      const neutralLabel = isLikeCompact ? t('Add to my list') : t('Sign up for the launch')
      return {
        status: 'neutral',
        label: neutralLabel,
        apiAction: 'like',
        roundButton: {
          text: neutralLabel,
          icon: 'inactive-heart',
          isCompact: isLikeCompact,
        },
      }
    }
  }
}

const LOCALSTORAGE_NAMESPACE = 'ul_modalShowedUp'

class ProjectsListItem extends React.Component {
  constructor(props) {
    super(props)
    const { project, isLikeCompact } = this.props

    if (project && project.user_role === undefined) return

    this.state = {
      ...getRelatedUserRole(project && project.user_role, isLikeCompact),
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const status = this.state && this.state.status
    const prevStatus = prevState && prevState.status
    if (status !== prevStatus) {
      if (this.props.project && this.props.project.user_role === undefined) return
      this.setState({
        ...getRelatedUserRole(USER_ROLE_STATUS[this.state.status], this.props.isLikeCompact),
      })
    }

    if (window.localStorage.getItem(LOCALSTORAGE_NAMESPACE) === 'true') {
      return {
        modalShowedUp: true,
      }
    }
  }

  onClick = (event) => {
    event.preventDefault()
    const { project, action, isAuthenticated } = this.props
    const { status } = this.state
    if (isAuthenticated) {
      if (status && (status === 'created' || status === 'backed')) return

      const newStatus = status && status === 'listed' ? 'neutral' : 'listed'

      this.setState({
        status: newStatus,
      })

      updateUserRole(project && project.id, this.state.apiAction).then(() => {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'socialInt',
            socialNetwork: 'Ulule',
            socialAction: newStatus === 'listed' ? 'Like' : 'Unlike',
            socialTarget: project && project.absolute_url,
            ululeProjectId: project && project.id,
          })
        }
        action.onSuccess(project, newStatus)
      }, action.onError)
      return
    }
    saveAction(project.id, this.state.apiAction)

    action.onError()
    return
  }

  isProjectAlmostEnded = () => {
    const { project } = this.props

    const FourtyHeightHours = 48 * 3600
    const projectEndDate = +new Date(project && project.date_end).toISOString()
    const today = +new Date().toISOString()

    if (today - projectEndDate < FourtyHeightHours) {
      return true
    }

    return false
  }

  handleCloseModal = () => {
    this.setState({
      isOpen: false,
    })
  }

  render() {
    const { currentLanguage, currentCurrency, rates, project, isLikeCompact } = this.props
    const progress = getProgress(project)
    let countdownText = ''
    if (project && project.date_end) {
      countdownText = getShortCountdown(project)
    }
    const isOnline = project && project.status === 'online'
    const isProjectFinished = project && project.finished

    const action =
      project && project.user_role !== undefined
        ? {
          ...this.state.roundButton,
          onClick: (event) => this.onClick(event),
        }
        : undefined

    let textFinishProject = t('Finished')
    if (isProjectFinished) {
      textFinishProject = getFinishTrad(project.date_end)
    }

    return (
      <React.Fragment>
        {project && (
          <ProjectCardWrapper>
            <duvet.CardTypeSelector
              mainImage={
                project &&
                project.main_image &&
                project.main_image.versions &&
                project.main_image.versions['2x'] &&
                project.main_image.versions['2x'].url
              }
              cardType="classic"
              action={action}
              overrideIsProjectOnline={isOnline}
              project={{ ...project, language: currentLanguage, main_image: { [project.lang]: project.main_image } }}
              rates={rates}
              targetCurrency={currentCurrency}
              projectTranslation={{
                backerCount: tn(
                  '%s contribution',
                  '%s contributions',
                  (project && project.supporters_count) || 0,
                  text.numberToLocaleString(project.orders_count || project.supporters_count),
                ),
                comingSoon: getComingSoonLabel(project, Boolean(window.UFE.user.is_staff)),
                extraTime: t('Extension'),
                finished: textFinishProject,
                highlights: t('Highlights'),
                progressInfo: t('%(percent)s reached', {
                  percent: `${text.numberToLocaleString(progress)}%`,
                }),
                shortCountdown: countdownText,
                status:
                  getStatus({
                    isOnline,
                    endDate: project.date_end,
                    extraTime: project.is_in_extra_time,
                    finished: project.finished,
                  }) ?? (isOnline ? countdownText : undefined),
              }}
            />
            {!isLikeCompact && <Button {...action} isCompact={false} />}
          </ProjectCardWrapper>
        )}
      </React.Fragment>
    )
  }
}

ProjectsListItem.propTypes = {
  project: PropTypes.shape({
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    absolute_url: PropTypes.string.isRequired,
    date_end: PropTypes.string,
    is_featured: PropTypes.bool,
  }).isRequired,
  currentLanguage: PropTypes.string.isRequired,
  currentCurrency: PropTypes.string.isRequired,
  isLikeCompact: PropTypes.bool.isRequired,
  rates: PropTypes.object.isRequired,
}

export default ProjectsListItem
